/**
 * Home all reusable commitment logic across MyCompassion UIs.
 * This might be good to moved to shared-utils.
 */
import { TFunction } from 'i18next';

import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { PaymentFrequencyType } from '@sola/schema/dotcom/proxy-api/transactions.js';
import { CommitmentCodeEnum } from '@sola/schema/dotcom/shared/commitments.ts';
import {
  GraphQLCommitment,
  GraphQLCommitmentType,
  GraphQLPledge,
} from '@sola/schema/dotcom/supporters/schema.generated.js';

import {
  isFundCodeBirthdayGift,
  isFundCodeCenterGift,
  isFundCodeChildGift,
  isFundCodeChildSponsorship,
  isFundCodeChristmasGift,
  isFundCodeFamilyGift,
} from '../atoms/commitment-avatar/commitment-code-type-guards';

/**
 * Format user-friendly commitment title to be used across various UIs.
 */
export const getCommitmentTitle = (
  commitment: GraphQLCommitment,
  t: TFunction<'my', undefined>,
): string => {
  // TODO: Title changes based on type of commitment
  // CDSP will display child's name
  // BG will display "{childName}'s Birthday"
  // Seems like other funds are displaying fund name

  if (commitment.fund?.code) {
    if (
      isFundCodeChildSponsorship(commitment.fund.code as CommitmentCodeEnum)
    ) {
      return commitment.participant?.name || '';
    }

    if (isFundCodeBirthdayGift(commitment.fund.code as CommitmentCodeEnum)) {
      return t('commitments.title.birthday', {
        childName: commitment.participant?.name,
      });
    }

    if (isFundCodeChildGift(commitment.fund.code as CommitmentCodeEnum)) {
      return t('commitments.title.child', {
        childName: commitment.participant?.name,
      });
    }

    if (isFundCodeFamilyGift(commitment.fund.code as CommitmentCodeEnum)) {
      return t('commitments.title.family', {
        childName: commitment.participant?.name,
      });
    }

    if (isFundCodeCenterGift(commitment.fund.code as CommitmentCodeEnum)) {
      return t('commitments.title.center');
    }
  }

  return commitment.fund?.description || '';
};

/**
 * Get abbreviation for commitment frequency to be used across various UIs.
 */
export const getFrequencyAbbr = (
  frequency: string,
  t: TFunction<'my', undefined>,
) => {
  switch (frequency) {
    case PaymentFrequencyType.Annually:
      return t('commitments.frequencyAbbr.annual');
    case PaymentFrequencyType.Monthly:
      return t('commitments.frequencyAbbr.monthly');
    case PaymentFrequencyType.Quarterly:
      return t('commitments.frequencyAbbr.quarterly');
    case PaymentFrequencyType.Semiannually:
      return t('commitments.frequencyAbbr.semiAnnual');
    default:
      return '';
  }
};

/**
 * Get commitment label for commitment type to be used across various UIs.
 */
export const getCommitmentLabel = (
  commitment: GraphQLCommitment,
  t: TFunction<'my', undefined>,
) => {
  if (isFundCodeChristmasGift(commitment.fund?.code as CommitmentCodeEnum)) {
    return t('commitments.category.fundCommitment');
  }

  switch (commitment.commitmentType) {
    case GraphQLCommitmentType.ChildSponsorship:
      return t('commitments.category.childSponsorship');
    // TODO: Make sure when integration with live data that this is needed or if the GiftCatalog is all we need
    case GraphQLCommitmentType.Intervention:
      return t('commitments.category.intervention');
    case GraphQLCommitmentType.FundCommitment:
      return t('commitments.category.fundCommitment');
    case GraphQLCommitmentType.GiftCommitment:
      return t('commitments.category.giftCommitment');
    case GraphQLCommitmentType.GiftCatalog:
      return t('commitments.category.fundCommitment');
    default:
      return '';
  }
};

export interface CommitmentCardOverrideData {
  title?: string | null;
  label?: string | null;
  rate?: string | number | null;
  footer?: string | null;
  pledge?: GraphQLPledge | null;
}

/**
 * Get recurring commitments from a list of commitments.
 */
export const getRecurringCommitments = (commitments: GraphQLCommitment[]) =>
  commitments?.length
    ? commitments
        .filter((commitment) => commitment.isRecurring)
        .sort((a, b) => {
          if (
            a.commitmentType === CommitmentTypeEnum.ChildSponsorship &&
            b.commitmentType !== CommitmentTypeEnum.ChildSponsorship
          ) {
            return -1;
          }
          if (
            a.commitmentType !== CommitmentTypeEnum.ChildSponsorship &&
            b.commitmentType === CommitmentTypeEnum.ChildSponsorship
          ) {
            return 1;
          }
          return 0;
        })
    : null;
